import { useEffect, useState } from 'react';
import Layout from '../../../layouts';
import Page from '../../../components/Page';
import { Num } from '../../../components/gps';
import TituloTela from '../../../components/gps/TituloTela';

/* ============================================================================
   Chave de Acesso - visual novo (shadcn + Tailwind).

   Esta tela foi REESCRITA NO LUGAR, sem rota paralela, porque nao ha nenhum
   risco a mitigar: ela nao chama a API, nao le e nao grava nada. E uma conta
   local - o mesmo calculo que ja estava aqui, preservado caractere por
   caractere:

       chave = (codigo * 113) + (codigo * 7) + 5

   O arquivo anterior tinha ~240 linhas, das quais a maior parte era importacao
   morta (Table, TablePagination, GuestGuard, PATH_AUTH, quatro `styled` de tela
   de login que nunca eram renderizados) herdada de copiar outra pagina. Isso
   sai; a conta fica.

   TRES MUDANCAS DE COMPORTAMENTO, todas deliberadas:

   1. O resultado ANTERIOR nao e mais apagado quando se digita o proximo codigo.
      Quem usa isto esta no telefone com alguem em campo e frequentemente precisa
      repetir a chave; limpar a tela ao digitar de novo obrigava a refazer.

   2. BOTAO COPIAR. A chave e ditada por telefone ou colada num chat - selecionar
      texto com o mouse e o passo onde se erra um digito.

   3. O campo aceita ENTER. Era botao-so, e um campo de um valor unico com botao
      ao lado sempre vai receber Enter.

   As cores saem dos tokens; antes eram `bg-gray-50`, `text-teal-900` e
   `dark:bg-gray-700` cravados, que ignoram o tema do sistema.

   ---------------------------------------------------------------------------
   20/08/2026 - A TELA GANHOU O PASSO A PASSO E O DESENHO DO APARELHO.

   Antes eram um campo e um botao no meio de uma tela vazia. Quem nunca usou nao
   tinha como saber DE ONDE vem o codigo (aparece no proprio aparelho, na tela de
   validacao) nem O QUE FAZER com a chave depois de gerada - e essa tela e usada
   justamente por quem esta ao telefone com alguem em campo, no momento em que o
   aparelho travou.

   O desenho e um celular em SVG mostrando as duas telas do fluxo (pedir o codigo /
   digitar a chave), alternando devagar. Ele nao e enfeite: mostra ONDE no aparelho
   o numero aparece, que e a pergunta de quem esta do outro lado da linha.

   `prefers-reduced-motion` desliga a alternancia pela regra global de
   theme/global.css; a tela continua legivel parada no primeiro passo.
   ============================================================================ */

/* Os tres passos. Ficam em constante porque a tela mostra os mesmos textos em dois
   lugares (a lista numerada e o rotulo do desenho), e texto repetido em dois
   lugares e texto que divergem no primeiro ajuste. */
const PASSOS = [
  {
    titulo: 'No aparelho, peça o código',
    detalhe: 'Na tela de bloqueio do GPShub, toque em "Liberar acesso". O aparelho mostra um código de 4 a 6 dígitos.',
  },
  {
    titulo: 'Digite o código aqui',
    detalhe: 'Informe os números no campo ao lado e gere a chave. O cálculo é feito no navegador — nada sai daqui.',
  },
  {
    titulo: 'Dite a chave para quem está com o aparelho',
    detalhe: 'A pessoa digita a chave no aparelho e o acesso é liberado. Confirme os dígitos repetindo em blocos.',
  },
];

function calcularChave(codigo: string) {
  const digitos = codigo.replace(/\D/g, '');
  if (!digitos) return null;
  const n = Number(digitos);
  if (!Number.isFinite(n)) return null;
  return String(n * 113 + n * 7 + 5);
}

/**
 * Celular em SVG mostrando as duas telas do fluxo.
 *
 * Desenhado a mao (nao e imagem) por dois motivos: acompanha os tokens de cor nos
 * dois temas, e pesa ~2 kB em vez de um print de 200 kB que envelhece na primeira
 * mudanca do app.
 *
 * A alternancia entre as telas usa `animate-[...]` com keyframes locais no
 * proprio SVG via CSS - as duas camadas trocam de opacidade em ciclo de 6s, o que
 * da tempo de LER cada uma. Ciclo curto viraria piscada.
 */
/* AS DUAS TELAS OCUPAM A MESMA REGIAO (10/09/2026).

   Elas se alternam por opacidade, entao nunca aparecem juntas - mas estavam
   desenhadas uma EMBAIXO da outra, em faixas verticais diferentes (52..182 e
   222..308). Era isso que obrigava o aparelho a ter 380 de altura: metade dele
   existia so para guardar a tela que naquele instante esta apagada.

   Sobrepostas, o mesmo conteudo cabe em 280 em vez de 380 - o aparelho
   encurtou um quarto sem perder nada, e o codigo de exemplo continua no
   tamanho em que se le de longe, que e o motivo de a coluna ter sido
   alargada. */
function CelularDemonstracao({ etapa }: { etapa: number }) {
  return (
    <svg viewBox="0 0 200 310" className="h-auto w-full max-w-[300px]" role="img" aria-label="Aparelho mostrando onde o código aparece">
      {/* corpo - 156 de largura para 264 de altura, ~1:1.7.

          Na versao anterior o aparelho tinha 184x234: quase um quadrado. Ao
          encurtar a altura eu mantive a largura de antes, e o desenho deixou de
          se parecer com um celular - lia como tablet, que e outro objeto. A
          proporcao aqui e a de um telefone real, e e ela que faz o desenho ser
          reconhecido sem legenda. */}
      <rect x="22" y="8" width="156" height="294" rx="24" fill="var(--gps-surface-2)" stroke="var(--gps-border)" strokeWidth="2" />
      <rect x="32" y="30" width="136" height="250" rx="10" fill="var(--gps-bg)" stroke="var(--gps-border)" />
      {/* alto-falante */}
      <rect x="84" y="18" width="32" height="5" rx="2.5" fill="var(--gps-border)" />

      {/* O CONTEUDO OCUPA A TELA INTEIRA (10/09/2026).

          Ao encurtar e depois alongar o aparelho, o desenho de dentro ficou do
          tamanho de antes e sobrava um terco de tela vazia embaixo - um celular
          com a interface empurrada para cima, que e o que nenhum celular faz.
          Os elementos foram redistribuidos de 56 a 244 dentro da tela util
          (30..280), com margem parecida em cima e embaixo, e o codigo cresceu:
          ele e o motivo de este desenho existir e agora se le de longe, com o
          telefone na mao. */}

      {/* ---------------------------------------------- tela 1: codigo no aparelho */}
      <g opacity={etapa === 2 ? 0 : 1} style={{ transition: 'opacity .5s' }}>
        <rect x="42" y="56" width="116" height="22" rx="6" fill="var(--gps-accent-soft)" />
        <text x="100" y="71" textAnchor="middle" fontSize="10.5" fill="var(--gps-muted)" fontFamily="inherit">
          Acesso bloqueado
        </text>

        <text x="100" y="118" textAnchor="middle" fontSize="10" fill="var(--gps-faint)" fontFamily="inherit">
          Informe ao suporte
        </text>
        <rect x="44" y="132" width="112" height="64" rx="10" fill="var(--gps-surface)" stroke="var(--gps-accent-line)" strokeWidth="1.5" />
        <text
          x="100"
          y="176"
          textAnchor="middle"
          fontSize="30"
          fontWeight="700"
          fill="var(--gps-accent)"
          fontFamily="inherit"
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          4821
        </text>
        <text x="100" y="228" textAnchor="middle" fontSize="9" fill="var(--gps-faint)" fontFamily="inherit">
          este é o código que você digita
        </text>
      </g>

      {/* ---------------------------------------- tela 2: digitar a chave recebida

          `opacity 0` e nao 0.15: sobrepostas, a tela apagada apareceria POR CIMA
          da ativa como texto fantasma. Empilhadas era so um esmaecido ao lado. */}
      <g opacity={etapa === 2 ? 1 : 0} style={{ transition: 'opacity .5s' }}>
        <text x="100" y="118" textAnchor="middle" fontSize="10" fill="var(--gps-faint)" fontFamily="inherit">
          Chave de liberação
        </text>
        <rect x="40" y="132" width="120" height="58" rx="10" fill="var(--gps-surface)" stroke="var(--gps-ok)" strokeWidth="1.5" />
        <text
          x="100"
          y="171"
          textAnchor="middle"
          fontSize="24"
          fontWeight="700"
          fill="var(--gps-ok)"
          fontFamily="inherit"
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {/* 578525 e a chave REAL do codigo 4821 mostrado na tela 1 (4821*113 +
              4821*7 + 5). Eu tinha escrito 577525 aqui, um numero inventado: numa
              tela que ensina a usar, exemplo com conta errada e pior que exemplo
              nenhum - alguem confere na mao e conclui que o sistema esta errado. */}
          578525
        </text>
        <rect x="52" y="214" width="96" height="30" rx="8" fill="var(--gps-accent)" />
        <text x="100" y="234" textAnchor="middle" fontSize="11" fontWeight="700" fill="var(--gps-accent-ink)" fontFamily="inherit">
          Liberar
        </text>
      </g>

      {/* botao inferior */}
      <rect x="80" y="288" width="40" height="4" rx="2" fill="var(--gps-border)" />
    </svg>
  );
}

ChaveAcesso.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout>{page}</Layout>;
};

export default function ChaveAcesso() {
  const [valor, setValor] = useState('');
  const [gerado, setGerado] = useState('');
  const [codigoGerado, setCodigoGerado] = useState('');
  const [aviso, setAviso] = useState('');
  const [copiado, setCopiado] = useState(false);

  /* Etapa que o desenho esta mostrando.

     Alterna sozinha a cada 6s enquanto nao houver chave gerada - e a demonstracao
     de "como funciona", para quem esta aprendendo. Depois de gerar, PARA na etapa 2
     (digitar a chave no aparelho), que e o que a pessoa tem para fazer naquele
     momento: animacao que continua rodando quando ja ha uma tarefa na tela e
     distracao.

     6s porque cada tela tem texto para ler; em 2s viraria piscada. E
     `prefers-reduced-motion` nao precisa de tratamento especial aqui: isto e
     `setInterval` com troca de opacidade via transicao CSS, que a regra global de
     global.css ja neutraliza - a tela fica parada na etapa 1, legivel. */
  const [etapa, setEtapa] = useState(1);

  useEffect(() => {
    if (gerado) {
      setEtapa(2);
      return undefined;
    }
    const t = setInterval(() => setEtapa((e) => (e === 1 ? 2 : 1)), 6000);
    return () => clearInterval(t);
  }, [gerado]);

  function gerar() {
    const chave = calcularChave(valor);
    if (!chave) {
      setAviso('Digite o código de acesso mostrado no aparelho.');
      return;
    }
    setAviso('');
    setCodigoGerado(valor.replace(/\D/g, ''));
    setGerado(chave);
    setCopiado(false);
    setValor('');
  }

  async function copiar() {
    try {
      await navigator.clipboard.writeText(gerado);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      // Sem clipboard (http em rede interna, por exemplo) o texto continua na
      // tela e selecionavel - nao vale mostrar erro por causa disso.
    }
  }

  return (
    <Page title="Chave de Acesso">
      <div className="mx-auto w-full max-w-[var(--gps-pagina-max)] px-4 pb-2 pt-2 font-sans text-ink">
        <div className="mb-5">
          <TituloTela
            descricao={
              <>
Informe o código que aparece no aparelho e gere a chave de validação. O cálculo é feito aqui
            mesmo, no navegador — nada é enviado ao servidor.
              </>
            }
          >
            Chave de acesso
          </TituloTela>
        </div>

      {/* A COLUNA DA DIREITA FICOU MAIS LARGA (10/09/2026, a pedido).

          Eram 320px para um cartao cuja razao de existir e um desenho de
          aparelho - com o recuo interno, sobravam ~188px para a ilustracao, e o
          que ela precisa mostrar (o codigo na tela do aparelho, o campo da
          chave e o botao) ficava miudo demais para servir de referencia a quem
          esta ao telefone com o usuario.

          420px e o ponto em que o desenho chega a ~280px de largura sem que a
          coluna da esquerda, que tem campo de digitacao e passos, aperte: em
          1366 de largura ela ainda fica com folga. */}
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_420px]">
        <div className="rounded-lg border border-line bg-surface p-5 shadow-card">
          <label className="block">
            <span className="mb-1.5 block text-[12px] font-semibold">Código de acesso</span>
            <div className="flex gap-2">
              <input
                value={valor}
                onChange={(e) => {
                  setValor(e.target.value);
                  if (aviso) setAviso('');
                }}
                onKeyDown={(e) => e.key === 'Enter' && gerar()}
                inputMode="numeric"
                placeholder="Somente números"
                autoFocus
                className="h-10 flex-1 rounded-lg border border-line bg-surface px-3 tabular-nums text-[14px] tabular-nums text-ink outline-none placeholder:font-sans placeholder:text-faint focus:border-primary"
              />
              <button
                type="button"
                onClick={gerar}
                className="h-10 flex-none rounded-lg bg-primary px-5 text-[12.5px] font-bold text-primary-foreground"
              >
                Gerar
              </button>
            </div>
            {aviso && <span className="mt-1.5 block text-[12px] text-danger">{aviso}</span>}
          </label>

          {gerado && (
            <div className="mt-5 rounded-lg border border-line bg-surface-2 p-4">
              <div className="text-[11px] font-bold uppercase tracking-[0.07em] text-faint">
                Chave para o código <Num>{codigoGerado}</Num>
              </div>

              <div className="mt-2 flex items-center gap-3">
                <Num className="text-3xl font-extrabold leading-none tracking-tight text-ok">{gerado}</Num>
                <button
                  type="button"
                  onClick={copiar}
                  className="h-8 rounded-lg border border-line bg-surface px-3 text-[12px] font-semibold text-muted-foreground hover:text-ink"
                >
                  {copiado ? '✓ copiado' : 'copiar'}
                </button>
              </div>

              <p className="mt-3 text-[12px] text-muted-foreground">
                Dite os dígitos em blocos e confirme a repetição — a chave não tem verificação, um dígito
                trocado só aparece como recusa no aparelho.
              </p>
            </div>
          )}

          {/* PASSO A PASSO. Fica embaixo do campo, e nao acima: quem ja sabe usar
              vem para o campo e nao precisa passar os olhos por tres paragrafos
              toda vez. Quem nao sabe encontra logo abaixo, sem sair da tela.

              A numeracao aqui SIGNIFICA algo - e uma sequencia real, com ordem
              obrigatoria: sem o codigo do aparelho nao ha o que digitar. */}
          <ol className="mt-6 flex flex-col gap-3 border-t border-line pt-4">
            {PASSOS.map((passo, i) => (
              <li key={passo.titulo} className="flex gap-3">
                <span
                  className={`grid h-6 w-6 flex-none place-items-center rounded-full border text-[11px] font-bold tabular-nums ${
                    // O passo em curso fica destacado: enquanto nao ha chave, e o 1;
                    // depois de gerar, e o 3 (dite a chave), que e o que falta fazer.
                    (gerado ? 2 : 0) === i
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-line text-muted-foreground'
                  }`}
                >
                  {i + 1}
                </span>
                <span className="min-w-0">
                  <span className="block text-[12px] font-semibold">{passo.titulo}</span>
                  <span className="block text-[12px] leading-[1.5] text-muted-foreground">{passo.detalhe}</span>
                </span>
              </li>
            ))}
          </ol>
        </div>

        {/* COLUNA DO APARELHO. Some abaixo de lg: num celular, o desenho de um
            celular ocuparia a tela inteira antes do campo que resolve o problema. */}
        <aside className="hidden rounded-lg border border-line bg-surface-2 p-5 shadow-card lg:block">
          <div className="flex flex-col items-center gap-4">
            <CelularDemonstracao etapa={etapa} />

            <div className="text-center">
              <span className="block text-[12px] font-bold">
                {etapa === 1 ? 'O código nasce no aparelho' : 'A chave é digitada no aparelho'}
              </span>
              <span className="mt-0.5 block text-[11px] leading-[1.5] text-muted-foreground">
                {etapa === 1
                  ? 'Peça para a pessoa ler os dígitos que aparecem na tela de bloqueio.'
                  : 'Dite a chave gerada; ao confirmar, o aparelho libera o acesso.'}
              </span>
            </div>

            <div className="flex gap-1.5" role="tablist" aria-label="Etapa da demonstração">
              {[1, 2].map((n) => (
                <button
                  key={n}
                  type="button"
                  role="tab"
                  aria-selected={etapa === n}
                  aria-label={`Mostrar etapa ${n}`}
                  onClick={() => setEtapa(n)}
                  className={`h-1.5 w-6 rounded-full transition-colors ${etapa === n ? 'bg-primary' : 'bg-line'}`}
                />
              ))}
            </div>
          </div>

          <p className="mt-4 border-t border-line pt-3 text-[11px] leading-[1.55] text-faint">
            A chave vale para <strong className="text-muted-foreground">um</strong> código: se a pessoa pedir
            liberação de novo, o aparelho mostra outro número e a chave anterior não serve.
          </p>
        </aside>
      </div>
      </div>
    </Page>
  );
}
